/**
 * LeaveTypes — CRUD for the leave categories available to workers.
 * Migrated to live API in Wave 3.2. Standard pattern: fetchAll +
 * snake_case form + bespoke dialog for the icon/color picker +
 * shared ConfirmDialog for delete.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  Grid, IconButton, InputLabel, MenuItem, Select, Snackbar, Stack, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, BeachAccessOutlined, DeleteOutlined, EditOutlined,
  EventBusyOutlined, EventNoteOutlined, LocalHospitalOutlined,
  PersonOutlined, PregnantWomanOutlined, RefreshOutlined,
  SentimentDissatisfiedOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi } from '../../../api';
import type { LeaveType, LeaveTypeCreate } from '../../../api/attendance';
import { ConfirmDialog } from '../../../components/primitives';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';

const ICON_MAP: Record<string, React.ReactNode> = {
  beach: <BeachAccessOutlined fontSize="small" />,
  health: <LocalHospitalOutlined fontSize="small" />,
  person: <PersonOutlined fontSize="small" />,
  baby: <PregnantWomanOutlined fontSize="small" />,
  sad: <SentimentDissatisfiedOutlined fontSize="small" />,
  busy: <EventBusyOutlined fontSize="small" />,
};
const COLOR_OPTIONS = [
  '#4CAF50', '#FF9800', '#2E65D4', '#E91E63', '#607D8B', '#F44336',
  '#9C27B0', '#00BCD4',
];
const ICON_OPTIONS = Object.keys(ICON_MAP);

const EMPTY_FORM: LeaveTypeCreate = {
  name: '',
  description: '',
  paid: true,
  color: '#4CAF50',
  icon: 'beach',
  requires_approval: true,
  allow_half_day: false,
  allow_negative_balance: false,
};

const LeaveTypes: React.FC = () => {
  const { t } = useTranslation();
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LeaveType | null>(null);
  const [form, setForm] = useState<LeaveTypeCreate>({ ...EMPTY_FORM });
  const [pendingDelete, setPendingDelete] = useState<LeaveType | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTypes(await attendanceApi.leaveTypesApi.list());
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:leaveTypes.loadFailed'));
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleOpen = (item?: LeaveType) => {
    if (item) {
      setEditing(item);
      setForm({
        name: item.name,
        description: item.description ?? '',
        paid: item.paid,
        color: item.color ?? '#4CAF50',
        icon: item.icon ?? 'beach',
        requires_approval: item.requires_approval,
        allow_half_day: item.allow_half_day,
        allow_negative_balance: item.allow_negative_balance,
      });
    } else {
      setEditing(null);
      setForm({ ...EMPTY_FORM });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await attendanceApi.leaveTypesApi.update(editing.id, form);
        setToast(t('attendance:leaveTypes.toastUpdated', { name: form.name }));
      } else {
        await attendanceApi.leaveTypesApi.create(form);
        setToast(t('attendance:leaveTypes.toastCreated', { name: form.name }));
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:leaveTypes.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.leaveTypesApi.delete(pendingDelete.id);
      setToast(t('attendance:leaveTypes.toastDeleted', { name: pendingDelete.name }));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:leaveTypes.deleteFailed'));
      throw e;
    }
  };

  const iconFor = (k: string | null | undefined) =>
    (k && ICON_MAP[k]) || <EventNoteOutlined fontSize="small" />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 1.5, gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:leaveTypes.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:leaveTypes.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpen()}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('attendance:leaveTypes.addLeaveType')}</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : types.length === 0 ? (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <EventNoteOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('attendance:leaveTypes.noTypes')}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpen()}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('attendance:leaveTypes.addFirst')}</Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {types.map(lt => (
            <Grid item xs={12} md={4} key={lt.id}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ px: 2.5, py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: lt.color ?? '#4CAF50', flexShrink: 0 }} />
                      <Box sx={{ color: lt.color ?? 'text.primary', display: 'flex', alignItems: 'center' }}>{iconFor(lt.icon)}</Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{lt.name}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.25}>
                      <IconButton size="small" onClick={() => handleOpen(lt)}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                      <IconButton size="small" onClick={() => setPendingDelete(lt)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                    <Chip label={lt.paid ? t('attendance:leaveTypes.paid') : t('attendance:leaveTypes.unpaid')} size="small"
                      sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem', bgcolor: lt.paid ? 'success.light' : 'grey.200', color: lt.paid ? 'success.dark' : 'text.secondary' }} />
                    {lt.requires_approval && <Chip label={t('attendance:leaveTypes.approvalChip')} size="small" sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />}
                    {lt.allow_half_day && <Chip label={t('attendance:leaveTypes.halfDayChip')} size="small" sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{lt.description ?? '—'}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ m: 0, py: 2, fontWeight: 600, fontSize: '1rem' }}>{editing ? t('attendance:leaveTypes.editTitle') : t('attendance:leaveTypes.addTitle')}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField label={t('system:accessControl.nameLabel')} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} size="small" fullWidth />
            <TextField label={t('system:accessControl.descriptionLabel')} value={form.description ?? ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} size="small" fullWidth multiline rows={2} />
            <FormControl size="small" fullWidth>
              <InputLabel>Paid / unpaid</InputLabel>
              <Select value={form.paid ? 'paid' : 'unpaid'} label={t('attendance:leaveTypes.paidUnpaidLabel')} onChange={e => setForm(p => ({ ...p, paid: e.target.value === 'paid' }))}>
                <MenuItem value="paid">{t('attendance:leaveTypes.paid')}</MenuItem>
                <MenuItem value="unpaid">{t('attendance:leaveTypes.unpaid')}</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.75 }}>{t('attendance:leaveTypes.color')}</Typography>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {COLOR_OPTIONS.map(c => (
                  <Box key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                    sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: form.color === c ? '2px solid' : '2px solid transparent', borderColor: form.color === c ? 'text.primary' : 'transparent' }} />
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.75 }}>{t('attendance:leaveTypes.icon')}</Typography>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {ICON_OPTIONS.map(k => (
                  <IconButton key={k} size="small" onClick={() => setForm(p => ({ ...p, icon: k }))}
                    sx={{ border: form.icon === k ? '2px solid' : '1px solid transparent', borderColor: form.icon === k ? 'primary.main' : 'divider', borderRadius: 1 }}>
                    {ICON_MAP[k]}
                  </IconButton>
                ))}
              </Box>
            </Box>
            <FormControlLabel control={<Switch size="small" checked={!!form.requires_approval} onChange={e => setForm(p => ({ ...p, requires_approval: e.target.checked }))} />}
              label={<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t('attendance:leaveTypes.requiresApproval')}</Typography>} />
            <FormControlLabel control={<Switch size="small" checked={!!form.allow_half_day} onChange={e => setForm(p => ({ ...p, allow_half_day: e.target.checked }))} />}
              label={<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t('attendance:leaveTypes.allowHalfDay')}</Typography>} />
            <FormControlLabel control={<Switch size="small" checked={!!form.allow_negative_balance} onChange={e => setForm(p => ({ ...p, allow_negative_balance: e.target.checked }))} />}
              label={<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t('attendance:leaveTypes.allowNegativeBalance')}</Typography>} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} size="small" sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} size="small" disabled={!form.name?.trim() || saving}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {saving ? <CircularProgress size={18} /> : editing ? t('attendance:leaveTypes.saveChanges') : t('attendance:leaveTypes.addLeaveType')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:leaveTypes.deleteTitle')}
        description={t('attendance:leaveTypes.deleteDescription')}
        subject={pendingDelete && <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{pendingDelete.name}</Box>}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveTypes;
